import { LineString } from "ol/geom";
import GeometryLayout from 'ol/geom/GeometryLayout';
import { Draw } from "ol/interaction";
import { unByKey } from "ol/Observable";
import { getArea, getLength } from 'ol/sphere';
import { Fill, Style } from "ol/style";
import CircleStyle from "ol/style/Circle";
import Stroke from "ol/style/Stroke";
import { Switchboard } from '../../lib/events';
import { BaseInteraction } from '../../lib/interaction/interactions';
import { Maybe, None, Some } from '../../lib/misc/option';
import { ToolMessage } from '../messages/ToolMessage';
import { Tooltip } from './tooltip/Tooltip';
export class AreaInteraction extends BaseInteraction {
    constructor(map, layer) {
        super();
        this.map = map;
        this.layer = layer;
        this.active = None.instance();
        this.listener = None.instance();
        this.name = "area";
        this.zoom = 0;
        this.broadcaster = Switchboard.get_emitter("INTERACTION");
        this.emitter = Switchboard.get_emitter("CONTEXT");
        this.m_areas = [];
        this.m_lengths = [];
        this.get_area_in_sq_ft = (area_in_meters) => area_in_meters * 10.76391041671;
        this.get_area_in_acres = (area_in_sq_ft) => area_in_sq_ft / 43560;
        this.get_area_in_sq_mi = (area_in_sq_ft) => area_in_sq_ft / 27878400;
        this.get_area_in_sq_meters = (polygon) => { var _a, _b; return getArea(polygon, { projection: (_b = (_a = this.map.getView()) === null || _a === void 0 ? void 0 : _a.getProjection()) !== null && _b !== void 0 ? _b : 'EPSG:3857' }); };
        this.get_length_in_ft = (length_in_meters) => length_in_meters / 0.3048;
        this.get_length_in_mi = (length_in_ft) => length_in_ft / 5280;
        this.get_length_in_meters = (line) => { var _a, _b; return getLength(line, { projection: (_b = (_a = this.map.getView()) === null || _a === void 0 ? void 0 : _a.getProjection()) !== null && _b !== void 0 ? _b : 'EPSG:3857' }); };
        this.enter_context = new Some("toolbar");
        this.exit_context = new Some("revert");
        this.zoom = Math.floor(this.map.getView().getZoom() ? this.map.getView().getZoom() : Number(5));
        this.setupZoomListener(this.map);
    }
    init() {
        super.init();
        let draw = this.create_draw();
        this.attach_abort_handler(draw);
        this.interactions.push(draw);
        new ToolMessage({ selected: true, tool: this.name, command: "TOOL SELECTION" }).send();
    }
    remove() {
        this.remove_abort_handler();
        this.active = None.instance();
        this.listener = None.instance();
        super.remove();
        new ToolMessage({ selected: false, tool: this.name, command: "TOOL SELECTION" }).send();
    }
    create_draw() {
        const draw = new Draw({
            source: this.layer.source,
            type: "Polygon",
            condition: (e) => {
                let evt = e.originalEvent;
                if (evt instanceof MouseEvent) {
                    if (evt.button !== 3)
                        return true;
                }
                return false;
            },
            style: new Style({
                stroke: new Stroke({
                    color: 'rgba(255,204,51,0.5)',
                    lineDash: [10, 10],
                    width: 2
                }),
                image: new CircleStyle({
                    radius: 5,
                    stroke: new Stroke({
                        color: 'rgba(255,204,51,0.7)'
                    }),
                    fill: new Fill({
                        color: 'rgba(255,255,255,0.6)'
                    })
                })
            })
        });
        draw.on('drawstart', this.draw_start_handler.bind(this));
        draw.on('drawend', this.draw_end_handler.bind(this));
        draw.on('drawabort', () => {
            this.cleanup_after_abort();
        });
        return draw;
    }
    setupZoomListener(map) {
        map.getView().on('change:resolution', () => {
            this.zoom = Math.floor(map.getView().getZoom());
            this.m_areas.forEach(a => {
                a.area_tooltip.update_text(this.format_area(a.area_polygon));
            });
            this.m_lengths.forEach(l => {
                const div = l.tooltip.element.get(0);
                if (div) {
                    div.style.display = "block";
                }
            });
            this.m_lengths.forEach(l => {
                l.tooltip.update_text(this.format_length(l.lineString));
                const div1 = l.tooltip.element.get(0);
                if (!div1)
                    return;
                for (let i = 0; i < this.m_lengths.length; i++) {
                    if (l === this.m_lengths[i])
                        continue;
                    const div2 = this.m_lengths[i].tooltip.element.get(0);
                    if (!div2)
                        continue;
                    if (this.are_labels_overlapping(div1, div2)) {
                        div1.style.display = "none";
                        div2.style.display = "none";
                    }
                }
            });
        });
    }
    draw_start_handler(evt) {
        let sketch = evt.feature;
        let tooltip = new Tooltip(sketch);
        tooltip.element.css('text-align', 'center');
        this.active = new Some(tooltip);
        this.layer.add_tooltip(tooltip);
        this.map.addOverlay(tooltip.overlay);
        this.m_areas.push({ area_polygon: sketch.getGeometry(), area_tooltip: tooltip });
        const geo = Maybe(sketch.getGeometry());
        geo.map(geom => {
            const listener = geom.on('change', (evt) => {
                let geometry = evt.target;
                let interior = geometry.getFirstCoordinate();
                let output = this.format_area(geometry);
                tooltip.update_position(interior);
                tooltip.update_text(output);
            });
            this.listener = new Some(listener);
        });
    }
    draw_end_handler(evt) {
        let feature = evt.feature;
        feature.setStyle(new Style({
            stroke: new Stroke({
                color: 'rgba(255,204,51,1.0)',
                width: 2
            }),
        }));
        let geometry = feature.getGeometry();
        let coordinates = [];
        geometry.getCoordinates().forEach(c => {
            coordinates.push(...c);
        });
        for (let i = 0; i < coordinates.length - 1; i++) {
            const startPoint = coordinates[i];
            const endPoint = coordinates[i + 1];
            if (coordinates.length === 4) {
                this.active.unwrap().overlay.setOffset([0, -10]);
                this.active.unwrap().update_position(coordinates[0]);
            }
            else {
                let position = this.active.unwrap().overlay.getPosition();
                if (geometry.containsXY(position[0], position[1])) {
                    let factor = this.zoom >= 9 ? -100 : 100;
                    let x_coord = coordinates[0][0] + factor;
                    let y_coord = coordinates[0][1] + factor;
                    let new_coord = [x_coord, y_coord];
                    this.active.unwrap().update_position(new_coord);
                }
            }
            const lineString = new LineString([...startPoint, ...endPoint], GeometryLayout.XY);
            let tooltip = new Tooltip(feature);
            this.layer.add_tooltip(tooltip);
            this.map.addOverlay(tooltip.overlay);
            this.m_lengths.push({ lineString: lineString, tooltip: tooltip });
            if (coordinates.length === 4) {
                let yOffset = ((Math.random() * 100) + 1) % 50;
                tooltip.overlay.setOffset([0, yOffset]);
            }
            else {
                tooltip.overlay.setOffset([0, 0]);
            }
            let interior = lineString.getFlatMidpoint();
            tooltip.update_position(interior);
            let output = this.format_length(lineString);
            tooltip.update_text(output);
        }
        this.active.map(tt => tt.make_static(this.layer));
        this.active = None.instance();
        this.listener.map(l => unByKey(l));
        this.listener = None.instance();
    }
    format_area(polygon) {
        const area = this.get_area_in_sq_meters(polygon);
        const area_in_sq_ft = this.get_area_in_sq_ft(area);
        const area_in_acres = this.get_area_in_acres(area_in_sq_ft);
        if (area_in_sq_ft > 27878400) {
            const area_in_sq_mi = this.get_area_in_sq_mi(area_in_sq_ft);
            return this.format_number_string(Math.round(area_in_sq_mi * 100) / 100) + ' sq. mi';
        }
        return this.format_number_string(Math.round(area_in_sq_ft * 100) / 100) + ' sq. ft<br>' +
            this.format_number_string(Math.round(area_in_acres * 100) / 100) + ' acres';
    }
    format_length(line) {
        const length = this.get_length_in_meters(line);
        const length_ft = this.get_length_in_ft(length);
        if (length_ft > 5280)
            return this.format_number_string(Math.round(this.get_length_in_mi(length_ft) * 100) / 100) + ' mi';
        return this.format_number_string(Math.round(length_ft * 100) / 100) + " ft";
    }
    are_labels_overlapping(div1, div2) {
        const rect1 = div1.getBoundingClientRect();
        const rect2 = div2.getBoundingClientRect();
        return !(rect1.right < rect2.left ||
            rect1.left > rect2.right ||
            rect1.bottom < rect2.top ||
            rect1.top > rect2.bottom);
    }
    attach_abort_handler(draw) {
        $(window).on('keyup', (evt) => {
            if (evt.key == "Escape") {
                draw.abortDrawing();
                this.cleanup_after_abort();
            }
        });
    }
    remove_abort_handler() {
        $(window).off('keyup');
    }
    cleanup_after_abort() {
        if (this.active.isSome()) {
            this.active.unwrap().remove_from(this.layer);
        }
        this.remove_abort_handler();
        this.broadcaster.emit('i_request', '');
        this.emitter.emit('ctx_request', 'singleton');
    }
}
